import React, { useState, useEffect } from "react";
import api from "../../services/api";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import { useMyContext } from "../../store/ContextApi";
import { motion } from "framer-motion";
import { MdEmail } from "react-icons/md";
import { FaPaperPlane } from "react-icons/fa";
import { HiArrowLeft } from "react-icons/hi";
import { useDispatch } from "react-redux";
import { sendResetPasswordToken } from "../../State/Auth/Action";
import { useSelector } from "react-redux";

const ForgotPassword = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { token } = useMyContext();
  const dispatch = useDispatch();

  const {
    sentForgotPasswordTokenResult,
    sentForgotPasswordTokenLoading,
    sentForgotPasswordTokenError,
  } = useSelector((state) => state.auth);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: "",
    },
    mode: "onTouched",
  });

  const onPasswordForgotHandler = (data) => {
    const { email } = data;

    setLoading(true);
    const formData = new URLSearchParams();
    formData.append("email", email);
    dispatch(sendResetPasswordToken(email));
  };

  useEffect(() => {
    if (token) navigate("/");
  }, [token, navigate]);

  useEffect(() => {
    if (sentForgotPasswordTokenResult?.success) {
      toast.success("Password reset email sent! Check your inbox.");
      reset();
      setLoading(false);
    } else if (sentForgotPasswordTokenError?.message) {
      toast.error(
        sentForgotPasswordTokenError.message || "Failed to send email."
      );
    }
  }, [dispatch, sentForgotPasswordTokenResult, sentForgotPasswordTokenError]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-300 via-pink-200 to-sky-500 flex items-center justify-center relative overflow-hidden">
      {/* Animated floating circles background */}
      <motion.div
        className="absolute top-[-100px] left-[-100px] w-72 h-72 bg-pink-300 rounded-full opacity-30 filter blur-3xl"
        animate={{ x: 50, y: 50 }}
        transition={{ duration: 6, yoyo: Infinity }}
      />
      <motion.div
        className="absolute bottom-[-100px] right-[-100px] w-72 h-72 bg-sky-300 rounded-full opacity-30 filter blur-3xl"
        animate={{ x: -50, y: -50 }}
        transition={{ duration: 6, yoyo: Infinity }}
      />

      <motion.form
        onSubmit={handleSubmit(onPasswordForgotHandler)}
        className="backdrop-blur-lg bg-white/10 border border-white/20 shadow-2xl rounded-3xl p-8 w-full max-w-md flex flex-col gap-6 z-10"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 300 }}
            className="flex justify-center mb-2"
          >
            <MdEmail className="text-5xl text-white" />
          </motion.div>
          <h1 className="text-2xl font-bold text-white">Forgot Password?</h1>
          <p className="text-sm text-gray-600 mt-1">
            Enter your email and we’ll send you a reset link
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <div className="relative">
            <input
              id="email"
              type="email"
              placeholder="Enter your email"
              {...register("email", { required: "*Email is required" })}
              className="w-full px-12 py-3 rounded-xl bg-white/80 text-gray-700 focus:outline-none focus:ring-4 focus:ring-pink-300 placeholder-gray-500"
            />
            <MdEmail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-xl" />
          </div>
          {errors.email && (
            <p className="text-red-300 text-xs">{errors.email.message}</p>
          )}
        </div>

        <motion.button
          type="submit"
          disabled={loading}
          className="w-full flex justify-center items-center gap-2 bg-pink-500 hover:bg-pink-600 text-white font-bold py-3 rounded-xl transition-all duration-300 shadow-lg hover:shadow-pink-300 disabled:bg-pink-300"
          whileHover={{ scale: 1.05 }}
        >
          {loading ? (
            "Sending..."
          ) : (
            <>
              <FaPaperPlane className="text-lg" />
              Send Reset Link
            </>
          )}
        </motion.button>

        <Link
          to="/login"
          className="flex items-center justify-center text-blue-600 text-sm hover:underline gap-1 mt-2"
        >
          <HiArrowLeft />
          Back to Login
        </Link>
      </motion.form>
    </div>
  );
};

export default ForgotPassword;
